<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
  CMD_WOOW_CREATE_LEAD,
  CMD_WOOW_CREATE_TICKET,
  CMD_WOOW_LINK_CUSTOMER,
} from 'dashboard/helper/commandbar/events';

import CreateTicketDialog from './woow/CreateTicketDialog.vue';
import CreateLeadDialog from './woow/CreateLeadDialog.vue';
import LinkCustomerDialog from './woow/LinkCustomerDialog.vue';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const ticketDialogRef = ref(null);
const leadDialogRef = ref(null);
const linkCustomerDialogRef = ref(null);

const currentChat = computed(() => store.getters.getSelectedChat);
const contactName = computed(() => currentChat.value?.meta?.sender?.name ?? '');

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  items.push(
    {
      icon: 'i-lucide-ticket',
      label: t('WOOW_CRM.ACTIONS.CREATE_TICKET'),
      action: 'woow_create_ticket',
      value: 'woow_create_ticket',
    },
    {
      icon: 'i-lucide-sprout',
      label: t('WOOW_CRM.ACTIONS.CREATE_LEAD'),
      action: 'woow_create_lead',
      value: 'woow_create_lead',
    },
    {
      icon: 'i-lucide-link',
      label: t('WOOW_CRM.ACTIONS.LINK_CUSTOMER'),
      action: 'woow_link_customer',
      value: 'woow_link_customer',
    }
  );

  return items;
});

// The message context menu raises this with the message it was opened on, so a
// ticket can quote the exact line the customer complained about.
const openTicketDialog = (message = null) =>
  ticketDialogRef.value?.open(message);
const openLeadDialog = () => leadDialogRef.value?.open();
const openLinkCustomerDialog = () => linkCustomerDialogRef.value?.open();

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'woow_create_ticket') {
    openTicketDialog();
  } else if (action === 'woow_create_lead') {
    openLeadDialog();
  } else if (action === 'woow_link_customer') {
    openLinkCustomerDialog();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);
emitter.on(CMD_WOOW_CREATE_TICKET, openTicketDialog);
emitter.on(CMD_WOOW_CREATE_LEAD, openLeadDialog);
emitter.on(CMD_WOOW_LINK_CUSTOMER, openLinkCustomerDialog);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
  emitter.off(CMD_WOOW_CREATE_TICKET, openTicketDialog);
  emitter.off(CMD_WOOW_CREATE_LEAD, openLeadDialog);
  emitter.off(CMD_WOOW_LINK_CUSTOMER, openLinkCustomerDialog);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <CreateTicketDialog
      ref="ticketDialogRef"
      :conversation-id="currentChat.id"
      :contact-name="contactName"
    />
    <CreateLeadDialog ref="leadDialogRef" :conversation-id="currentChat.id" />
    <LinkCustomerDialog
      ref="linkCustomerDialogRef"
      :conversation-id="currentChat.id"
    />
  </div>
</template>
